/**
 * PaymentsView Component
 *
 * Purpose: Contains the Payment Activity modules
 *
 * @author Marisol Torres <marisol@inclancommunications.com>
 * @date 22/02/2022 12:51:25 PM
 */

import React, { useContext, useEffect, useState } from "react";
import { Container, Nav, Tab, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { goToHref, userInfo, isFeatureEnabled } from "../../components/global/GlobalTools";
import { GetHideMenus } from "../../components/global/GlobalTools";
import PropTypes from "prop-types";
import { NavTabsContext } from "../../components/DashboardLayout";
import NavDropdown from "react-bootstrap/NavDropdown";
import FeatureFlagWrapper from "../../components/global/FeatureFlagWrapper";
/** Services */
import Select from "react-select";
/** Elements */
import { MainContext } from "../../App";

/** Views */
import TransactionsReportView from "./TransactionsReportView";
import DebtReportView from "./DebtReportView";
import IncomeView from "./IncomeView";
import ScholarshipStudent from "./ScholarshipStudents ";
import { useHistory } from "react-router";
import TypeScholarships from "./TypeScholarships";
import SubconceptsView from "../../components/payments/SubconceptsView";
import OverdueDebtsView from "./OverdueDebtsView";

const PaymentsView = (props) => {
  const { selectedTab } = useContext(NavTabsContext);
  const { permissions } = useContext(MainContext);
  const prefix = process.env.REACT_APP_PREFIX;
  const [t] = useTranslation(["reports", "global", "scholarships"]); //In the array that is passed as an argument in useTranslation, the translations are loaded per module
  const [language] = useState(localStorage.getItem(`cmLanguage${prefix}`));
  const hideMenus = parseInt(GetHideMenus());
  const showNavItem = hideMenus !== 1; // to know when to show/hide the nav when it comes from the mobile app
  let previusTab =
    props.match.params && props.match.params.view
      ? props.match.params.view
      : "transactions";
  const [refreshTabContent, setRefreshTabContent] = useState(false);
  const [focusedTab, setFocusedTab] = useState(previusTab);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const history = useHistory();
  const mobileViewEnabled = isFeatureEnabled("Vista_Movil_Subconceptos");
  //Obtain basic user information (role, id and token)
  const basicUserData = userInfo();
  /**
   * Purpose: Redirect Cash register, Account Status, Transactions/Invoices, Subconcepts and cards to App1
   * @param {String} tabView
   */
  const goApp1 = (tabView) => {
    var now = new Date();
    now.setDate(now.getDate() + 5);
    goToHref(`/payments/${tabView}`);
  };

  /**Initial loading */
  useEffect(() => {
    const availableTabs = [
      "transactions",
      "debt",
      "income",
      "scholarships",
      "typeScholarships",
      "subconcepts",
      "overdueDebts",
    ];
    if (!availableTabs.includes(focusedTab)) {
      history.push("/payments");
    }
    document.title =
      language == "spanish" ? "Pagos - Adeudos" : "Payments - Debts";
  }, []);

  useEffect(() => {
    setRefreshTabContent(false);
  }, [selectedTab]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const buildOptions = () => {
    const opts = [];
    const opt = (value, labelKey, ns, action) => ({
      value,
      id: `sectionOption_${value}`,
      label: ns ? t(`${ns}:${labelKey}`) : t(labelKey),
      color: "#e74c3c",
      action,
    });
    const groupedOpt = (value, labelKey, ns, action) => ({
      ...opt(value, labelKey, ns, action),
      isGrouped: true,
    });

    if (permissions.payments_cashier.access) {
      opts.push({
        value: "cashRegister",
        id: "sectionOption_cashRegister",
        label: basicUserData.isParent || basicUserData.isStudent
          ? t("paymentsTabs.makePayment")
          : t("paymentsTabs.cashRegister"),
        color: "#e74c3c",
        action: () => goApp1("cashRegister"),
      });
    }
    if (permissions.payments_account_status.access) {
      opts.push(opt("statementAccount", "paymentsTabs.statementAccount", null, () => goApp1("accountStatus")));
    }
    if (permissions.payments_transactions.access) {
      opts.push(opt("transactions", "paymentsTabs.transactionsInvoices", null, () => {
        setFocusedTab("transactions");
        history.push("/payments/transactions");
      }));
    }
    if (showNavItem && permissions.payments_debts.access) {
      if (isFeatureEnabled("Notificaciones_adeudos_por_vencer")) {
        opts.push({
          label: t("reports.debt"),
          id: "sectionGroup_debt",
          options: [
            groupedOpt("debt", "reports.debt", null, () => { setFocusedTab("debt"); history.push("/payments/debt"); }),
            groupedOpt("overdueDebts", "paymentsTabs.overdueDebts", "reports", () => { setFocusedTab("overdueDebts"); history.push("/payments/overdueDebts"); }),
          ],
        });
      } else {
        opts.push(opt("debt", "reports.debt", null, () => { setFocusedTab("debt"); history.push("/payments/debt"); }));
      }
    }
    if (showNavItem && permissions.payments_incomes.access) {
      opts.push(opt("income", "paymentsTabs.income", null, () => { setFocusedTab("income"); history.push("/payments/income"); }));
    }
    if (showNavItem && permissions.payments_subconcepts.access) {
      opts.push(opt("subconcepts", "paymentsTabs.subconcepts", null, () => { setFocusedTab("subconcepts"); history.push("/payments/subconcepts"); }));
    }
    if (showNavItem && (permissions.payments_students_scholarships.access || permissions.payments_scholarships.access)) {
      opts.push({
        label: t("scholarships:textTitle.titleScholarships"),
        id: "sectionGroup_scholarships",
        options: [
          groupedOpt("scholarships", "textTitle.titleScholarshipRecipients", "scholarships", () => { setFocusedTab("scholarships"); history.push("/payments/scholarships"); }),
          groupedOpt("typeScholarships", "tabBar.typeScholarships", "scholarships", () => { setFocusedTab("typeScholarships"); history.push("/payments/typeScholarships"); }),
        ],
      });
    }
    if (permissions.payments_cards.access) {
      opts.push(opt("myCards", "paymentsTabs.myCards", null, () => goApp1("cards")));
    }
    return opts;
  };

  const findOption = (opts, val) => {
    for (const opt of opts) {
      if (opt.options) {
        const found = opt.options.find((o) => o.value === val);
        if (found) return found;
      } else if (opt.value === val) {
        return opt;
      }
    }
    return null;
  };

  const options = buildOptions();

  const formatGroupLabel = (group) => (
    <span id={group.id} style={{ color: "#212529", fontWeight: "bold" }}>
      {group.label}
    </span>
  );

  const formatOptionLabel = (option, { context }) => (
    <span
      id={context === "menu" ? option.id : undefined}
      style={{ color: context === "value" ? "#fff" : option.color || "inherit" }}
    >
      {option.label}
    </span>
  );

  const sectionSelector = () => (
    <Select
      id="selectTab"
      inputId="sectionSelector"
      options={options}
      value={findOption(options, focusedTab)}
      onChange={(selected) => selected?.action?.()}
      formatGroupLabel={formatGroupLabel}
      formatOptionLabel={formatOptionLabel}
      styles={{
        control: (base) => ({ ...base, minHeight: '30px', height: '30px', backgroundColor: '#ea2c54', borderColor: '#ea2c54' }),
        valueContainer: (base) => ({ ...base, height: '30px', padding: '0 6px' }),
        input: (base) => ({ ...base, margin: '0', padding: '0', color: '#fff' }),
        placeholder: (base) => ({ ...base, color: '#fff' }),
        dropdownIndicator: (base) => ({ ...base, color: '#fff', ':hover': { color: '#fff' } }),
        indicatorSeparator: () => ({ display: "none" }),
        indicatorsContainer: (base) => ({ ...base, height: '30px', padding: "0px, 5px" }),
        menu: (base) => ({ ...base, width: '93%' }),
        option: (base, { data, isFocused, isSelected }) => ({
          ...base,
          paddingLeft: data.isGrouped ? "24px" : base.paddingLeft,
          backgroundColor: isFocused || isSelected ? '#ffd9e0' : base.backgroundColor,
        }),
      }}
    />
  );


  return (
    <Container fluid>
      <Row>
        <Col md={12}>
          <h2 id="title">{t("scholarships:tabBar." + focusedTab)}</h2>
        </Col>
      </Row>
      <Row>
        {isMobile && mobileViewEnabled && sectionSelector()}
      </Row>
      {!refreshTabContent && (
        <Tab.Container id="payment" activeKey={focusedTab}>
          <Nav
            variant="pills"
            activeKey={focusedTab}
            style={{ width: "fit-content", margin: "0 auto" }}
            hidden={isMobile && mobileViewEnabled}
          >
            {/** This code will be used for transactions */}
            {permissions.payments_cashier.access && (
              <Nav.Item>
                <a
                  href={`${process.env.REACT_APP_V1}/payments/cashRegister`}
                  style={{ textDecoration: "none" }}
                >
                  <Nav.Link
                    eventKey="cashRegister"
                    onClick={() => goApp1("cashRegister")}
                  >
                    {basicUserData.isParent || basicUserData.isStudent
                      ? t("paymentsTabs.makePayment")
                      : t("paymentsTabs.cashRegister")}
                  </Nav.Link>
                </a>
              </Nav.Item>
            )}
            {/**Account status tab button*/}
            {permissions.payments_account_status.access && (
              <Nav.Item>
                <a
                  href={`${process.env.REACT_APP_V1}/payments/accountStatus`}
                  style={{ textDecoration: "none" }}
                >
                  <Nav.Link
                    eventKey="statementAccount"
                    onClick={() => goApp1("accountStatus")}
                  >
                    {t("paymentsTabs.statementAccount")}
                  </Nav.Link>
                </a>
              </Nav.Item>
            )}
            {/** Transactions/Invoices tab button */}
            {permissions.payments_transactions.access && (
              <Nav.Item>
                <a
                  href="/payments/transactions"
                  style={{ textDecoration: "none" }}
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                >
                  <Nav.Link
                    eventKey="transactions"
                    onClick={() => {
                      setFocusedTab("transactions");
                      history.push("/payments/transactions");
                    }}
                  >
                    {t("paymentsTabs.transactionsInvoices")}
                  </Nav.Link>
                </a>
              </Nav.Item>
            )}
            {/* debts */}
            {showNavItem && permissions.payments_debts.access && (
              <FeatureFlagWrapper
                name="Notificaciones_adeudos_por_vencer"
                fallback={
                  <Nav.Item>
                    <a
                      href="/payments/debt"
                      style={{ textDecoration: "none" }}
                      onClick={(e) => {
                        e.preventDefault();
                      }}
                    >
                      <Nav.Link
                        eventKey="debt"
                        onClick={() => {
                          setFocusedTab("debt");
                          history.push("/payments/debt");
                        }}
                      >
                        {t("reports.debt")}
                      </Nav.Link>
                    </a>
                  </Nav.Item>
                }
              >
                <NavDropdown
                  title={t("reports.debt")}
                  id="nav-dropdown"
                  className={
                    focusedTab == "debt" || focusedTab == "overdueDebts"
                      ? "nav-dropdown-cm"
                      : ""
                  }
                >
                  <a
                    href="/payments/debt"
                    style={{ textDecoration: "none" }}
                    onClick={(e) => {
                      e.preventDefault();
                    }}
                  >
                    <NavDropdown.Item
                      eventKey="debt"
                      onClick={() => {
                        setFocusedTab("debt");
                        history.push("/payments/debt");
                      }}
                    >
                      {t("reports.debt")}
                    </NavDropdown.Item>
                  </a>

                  <a
                    href="/payments/overdueDebts"
                    style={{ textDecoration: "none" }}
                  >
                    <NavDropdown.Item
                      eventKey="overdueDebts"
                      onClick={() => {
                        setFocusedTab("overdueDebts");
                        history.push("/payments/overdueDebts");
                      }}
                    >
                      {t("reports:paymentsTabs.overdueDebts")}
                    </NavDropdown.Item>
                  </a>
                </NavDropdown>
              </FeatureFlagWrapper>
            )}
            {/* incomes */}
            {showNavItem && permissions.payments_incomes.access && (
              <Nav.Item>
                <a
                  href="/payments/income"
                  style={{ textDecoration: "none" }}
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                >
                  <Nav.Link
                    eventKey="income"
                    onClick={() => {
                      setFocusedTab("income");
                      history.push("/payments/income");
                    }}
                  >
                    {t("paymentsTabs.income")}
                  </Nav.Link>
                </a>
              </Nav.Item>
            )}
            {/* subconcepts */}
            {showNavItem && permissions.payments_subconcepts.access && (
              <Nav.Item>
                <a
                  href="/payments/subconcepts"
                  style={{ textDecoration: "none" }}
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                >
                  <Nav.Link
                    eventKey="subconcepts"
                    onClick={() => {
                      setFocusedTab("subconcepts");
                      history.push("/payments/subconcepts");
                    }}
                  >
                    {t("paymentsTabs.subconcepts")}
                  </Nav.Link>
                </a>
              </Nav.Item>
            )}
            {/* students_scholarships */}
            {showNavItem &&
              (permissions.payments_students_scholarships.access ||
                permissions.payments_scholarships.access) && (
                <NavDropdown
                  title={t("scholarships:textTitle.titleScholarships")}
                  id="nav-dropdown"
                  className={
                    focusedTab == "scholarships" ||
                      focusedTab == "typeScholarships"
                      ? "nav-dropdown-cm"
                      : ""
                  }
                >
                  <a
                    href="/payments/scholarships"
                    style={{ textDecoration: "none" }}
                  >
                    <NavDropdown.Item
                      eventKey="scholarships"
                      onClick={() => {
                        setFocusedTab("scholarships");
                        history.push("/payments/scholarships");
                      }}
                    >
                      {t("scholarships:textTitle.titleScholarshipRecipients")}
                    </NavDropdown.Item>
                  </a>
                  <a
                    href="/payments/typeScholarships"
                    style={{ textDecoration: "none" }}
                  >
                    <NavDropdown.Item
                      eventKey="typeScholarships"
                      onClick={() => {
                        setFocusedTab("typeScholarships");
                        history.push("/payments/typeScholarships");
                      }}
                    >
                      {t("scholarships:tabBar.typeScholarships")}
                    </NavDropdown.Item>
                  </a>
                </NavDropdown>
              )}
            {permissions.payments_cards.access && (
              <Nav.Item>
                <a
                  href={`${process.env.REACT_APP_V1}/payments/cards`}
                  style={{ textDecoration: "none" }}
                >
                  <Nav.Link eventKey="myCards" onClick={() => goApp1("cards")}>
                    {t("paymentsTabs.myCards")}
                  </Nav.Link>
                </a>
              </Nav.Item>
            )}
          </Nav>

          <div style={{ marginTop: "20px" }}>
            <Tab.Content>
              {focusedTab == "transactions" && (
                <Tab.Pane eventKey="transactions">
                  {/** Report Transactions **/}
                  <TransactionsReportView defaultTabView={focusedTab} />
                </Tab.Pane>
              )}
              {focusedTab == "debt" && (
                <Tab.Pane eventKey="debt">
                  {/** Report Debts **/}
                  <DebtReportView />
                </Tab.Pane>
              )}
              {focusedTab == "income" && (
                <Tab.Pane eventKey="income">
                  <IncomeView />
                </Tab.Pane>
              )}
              {focusedTab == "overdueDebts" && (
                <Tab.Pane eventKey="overdueDebts">
                  <OverdueDebtsView />
                </Tab.Pane>
              )}
              {focusedTab == "scholarships" && (
                <Tab.Pane eventKey="scholarships">
                  <ScholarshipStudent />
                </Tab.Pane>
              )}
              {focusedTab == "typeScholarships" && (
                <Tab.Pane eventKey="typeScholarships">
                  <TypeScholarships />
                </Tab.Pane>
              )}
              {focusedTab == "subconcepts" && (
                <Tab.Pane eventKey="subconcepts">
                  <SubconceptsView />
                </Tab.Pane>
              )}
            </Tab.Content>
          </div>
        </Tab.Container>
      )}
    </Container>
  );
};

export default PaymentsView;

PaymentsView.propTypes = {
  match: PropTypes.object,
};
